import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Button, Spinner, Alert, Table, Badge, ButtonGroup, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { format, addDays, subDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const ScheduleSummary = ({ currentEntidade, currentUser, currentUserRole, todosMedicos, onVacantSlotClick, refreshTrigger, getFeriadoNome, isDiaBloqueado }) => {
    const [viewMode, setViewMode] = useState('day'); // 'day' or 'week'
    const [currentDate, setCurrentDate] = useState(new Date());
    const [appointments, setAppointments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasBeenInitiated, setHasBeenInitiated] = useState(false); // NOVO: Estado para controlar a busca inicial

    // Obtém a configuração de horários da entidade de forma segura
    const horariosConfig = useMemo(() => currentEntidade?.agenda_horarios_gerados || {}, [currentEntidade]);

    // Cria uma lista única e ordenada de todos os horários possíveis na semana para renderizar as linhas da tabela
    const horariosParaRenderizar = useMemo(() => {
        if (!horariosConfig || Object.keys(horariosConfig).length === 0) {
            return [];
        }
        const todosOsHorarios = new Set();
        // Itera sobre a configuração de cada dia da semana
        Object.values(horariosConfig).forEach(dia => {
            if (dia) { // Verifica se a configuração do dia existe
                // Adiciona os horários de todos os períodos (Manhã, Tarde, Noite) ao Set
                Object.values(dia).flat().forEach(horario => todosOsHorarios.add(horario));
            }
        });
        return Array.from(todosOsHorarios).sort(); // Converte para array e ordena
    }, [horariosConfig]);

    const fetchAppointments = useCallback(async (startDate, endDate) => {
        if (!currentEntidade?.id) return;

        setIsLoading(true);
        setError(null);

        const params = {
            inicio: format(startDate, 'yyyy-MM-dd'),
            fim: format(endDate, 'yyyy-MM-dd'),
        };

        let url;
        if (currentUserRole === 'Representante Legal' || currentUserRole === 'Recepção') {
            url = `/api/agendamentos/entidade/${currentEntidade.id}/periodo`;
        } else if (currentUserRole === 'Médico' && currentUser?.id) {
            url = `/api/agendamentos/entidade/${currentEntidade.id}/medico/${currentUser.id}/periodo`;
        } else {
            setIsLoading(false);
            return;
        }

        try {
            const response = await axios.get(url, { params });
            setAppointments(response.data.agendamentos || []);
        } catch (err) {
            console.error("Erro ao buscar agendamentos para o resumo:", err);
            setError("Não foi possível carregar os agendamentos.");
            setAppointments([]);
        } finally {
            setIsLoading(false);
        }
    }, [currentEntidade, currentUser, currentUserRole]);

    useEffect(() => {
        if (!hasBeenInitiated) { // NOVO: Guarda para não buscar na montagem inicial
            return;
        }

        if (viewMode === 'day') {
            fetchAppointments(currentDate, currentDate);
        } else { // week
            const start = startOfWeek(currentDate, { weekStartsOn: 1 }); // Semana começa na Segunda
            const end = endOfWeek(currentDate, { weekStartsOn: 1 });
            fetchAppointments(start, end);
        }
    }, [currentDate, viewMode, fetchAppointments, hasBeenInitiated]); // NOVO: Adiciona hasBeenInitiated

    // Efeito para re-buscar os dados quando o gatilho de atualização for acionado
    useEffect(() => {
        if (refreshTrigger > 0 && hasBeenInitiated) {
            // Apenas re-busca se a busca inicial já foi feita
            fetchAppointments(currentDate, currentDate); // Re-busca para a data/semana atual
        }
    }, [refreshTrigger, hasBeenInitiated, fetchAppointments, currentDate]);

    // NOVO: Função para iniciar a busca de dados se ainda não tiver sido feita
    const initiateFetch = () => {
        if (!hasBeenInitiated) {
            setHasBeenInitiated(true);
        }
    };

    const handlePrev = () => {
        initiateFetch();
        setCurrentDate(prev => viewMode === 'day' ? subDays(prev, 1) : subDays(prev, 7));
    };

    const handleNext = () => {
        initiateFetch();
        setCurrentDate(prev => viewMode === 'day' ? addDays(prev, 1) : addDays(prev, 7));
    };

    const handleToday = () => {
        initiateFetch();
        setCurrentDate(new Date());
    };

    // NOVO: Handler unificado para a troca de modo de visualização
    const handleViewModeChange = (mode) => {
        initiateFetch();
        setViewMode(mode);
    };

    const getDoctorName = (medicoId) => {
        const medico = todosMedicos.find(m => m.id === medicoId);
        return medico ? `Dr(a). ${medico.nome.split(' ')[0]}` : 'Médico?';
    };

    const processedAppointments = useMemo(() => {
        const grouped = {};
        appointments.forEach(app => {
            const time = app.hora_marcada.slice(0, 5);
            const date = format(new Date(app.data_agendamento + 'T00:00:00'), 'yyyy-MM-dd');
            const key = `${date}_${time}`;
            if (!grouped[key]) {
                grouped[key] = [];
            }
            grouped[key].push(app);
        });
        return grouped;
    }, [appointments]);

    const renderDayView = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isPastDate = new Date(currentDate).setHours(0, 0, 0, 0) < today;

        // Verifica se é feriado ou dia bloqueado
        const feriadoNome = getFeriadoNome(currentDate);
        const isBlocked = isDiaBloqueado(currentDate);

        const diaDaSemanaKey = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'][currentDate.getDay()];
        const configDia = horariosConfig[diaDaSemanaKey];
        const horariosDoDia = configDia ? [...configDia['Manhã'], ...configDia['Tarde'], ...configDia['Noite']].sort() : [];

        if (!hasBeenInitiated) return null; // Não renderiza nada se a busca não foi iniciada

        if (feriadoNome) {
            return <Alert variant="info" className="mt-3 text-center">Feriado: {feriadoNome}</Alert>;
        }
        if (isBlocked) {
            return <Alert variant="warning" className="mt-3 text-center">Este dia está bloqueado para agendamentos.</Alert>;
        }

        if (horariosDoDia.length === 0 && !isLoading) {
            return <Alert variant="info" className="mt-3 text-center">Não há horários de atendimento configurados para este dia.</Alert>;
        }

        return (
            <Table bordered responsive="sm" className="schedule-summary-table">
                <thead>
                    <tr>
                        <th>Horário</th>
                        <th>Agendamentos</th>
                    </tr>
                </thead>
                <tbody>
                    {horariosDoDia.map(time => {
                        const key = `${format(currentDate, 'yyyy-MM-dd')}_${time}`;
                        const apps = processedAppointments[key];
                        const numMedicos = todosMedicos.length > 0 ? todosMedicos.length : 1;
                        const numApps = apps ? apps.length : 0;
                        const isFullyBooked = numApps >= numMedicos;
                        const isClickable = !isFullyBooked && onVacantSlotClick && !isPastDate && !feriadoNome && !isBlocked;
                        const busyDoctorIds = apps ? apps.map(app => app.medico_id) : [];

                        return (
                            <tr key={time}>
                                <td className="time-slot">{time}</td>
                                <td
                                    className={`appointment-slot ${apps ? 'has-appointment' : (isPastDate ? 'is-past' : 'is-vacant')}`}
                                    onClick={isClickable ? () => onVacantSlotClick(time, currentDate, busyDoctorIds) : undefined}
                                    style={isClickable ? { cursor: 'pointer' } : {}}
                                    title={isClickable ? 'Clique para agendar' : (isFullyBooked ? 'Todos os horários ocupados' : (isPastDate && !apps ? 'Horário passado' : (feriadoNome || 'Dia bloqueado')))}
                                >
                                    {apps ? (
                                        apps.map(app => (
                                            <div key={app.id} className="appointment-item">
                                                <Badge bg="primary" className="me-2">{app.paciente?.nome.split(' ')[0]}</Badge>
                                                {(currentUserRole === 'Representante Legal' || currentUserRole === 'Recepção') && (
                                                    <small className="text-muted">{getDoctorName(app.medico_id)}</small>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        isPastDate ? '-' : <span className="text-success">Vago</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        );
    };

    const renderWeekView = () => {
        const start = startOfWeek(currentDate, { weekStartsOn: 1 });
        const end = endOfWeek(currentDate, { weekStartsOn: 1 });
        const days = eachDayOfInterval({ start, end });
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (!hasBeenInitiated) return null; // Não renderiza nada se a busca não foi iniciada

        if (horariosParaRenderizar.length === 0 && !isLoading) {
            return <Alert variant="info" className="mt-3 text-center">Não há horários de atendimento configurados para esta semana.</Alert>;
        }

        return (
            <div className="table-responsive">
                <Table bordered className="schedule-summary-table week-view">
                    <thead>
                        <tr>
                            <th className="time-col-week">Horário</th>
                            {days.map(day => (
                                <th key={day.toString()} className={`text-center ${isSameDay(day, new Date()) ? 'bg-info-subtle' : ''}`}>
                                    {format(day, 'EEE', { locale: ptBR })}<br />
                                    <small>{format(day, 'dd/MM')}</small>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {horariosParaRenderizar.map(time => (
                            <tr key={time}>
                                <td className="time-slot time-col-week">{time}</td>
                                {days.map(day => {
                                    const key = `${format(day, 'yyyy-MM-dd')}_${time}`;
                                    const apps = processedAppointments[key];
                                    const isPastDate = new Date(day).setHours(0, 0, 0, 0) < today;
 
                                    const feriadoNome = getFeriadoNome(day);
                                    const isBlocked = isDiaBloqueado(day);

                                    const diaDaSemanaKey = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'][day.getDay()];
                                    const configDia = horariosConfig[diaDaSemanaKey];
                                    const isSlotValidForDay = configDia && (
                                        configDia['Manhã']?.includes(time) ||
                                        configDia['Tarde']?.includes(time) ||
                                        configDia['Noite']?.includes(time)
                                    );

                                    if (!isSlotValidForDay || feriadoNome || isBlocked) {
                                        return <td key={day.toString()} className="appointment-slot is-disabled-slot">{feriadoNome ? <Badge bg="info" className="w-100 h-100 d-flex align-items-center justify-content-center" title={feriadoNome}>{feriadoNome.split(' ')[0]}</Badge> : ''}</td>;
                                    }

                                    const numMedicos = todosMedicos.length > 0 ? todosMedicos.length : 1;
                                    const numApps = apps ? apps.length : 0;
                                    const isFullyBooked = numApps >= numMedicos;
                                    const isClickable = !isFullyBooked && onVacantSlotClick && !isPastDate && !feriadoNome && !isBlocked;
                                    const busyDoctorIds = apps ? apps.map(app => app.medico_id) : [];

                                    return (
                                        <td 
                                            key={day.toString()} 
                                            className={`appointment-slot ${apps ? 'has-appointment' : (isPastDate ? 'is-past' : 'is-vacant')}`}
                                            onClick={isClickable ? () => onVacantSlotClick(time, day, busyDoctorIds) : undefined}
                                            style={isClickable ? { cursor: 'pointer' } : {}}
                                            title={isClickable ? 'Clique para agendar' : (isFullyBooked ? 'Todos os horários ocupados' : (isPastDate && !apps ? 'Horário passado' : (feriadoNome || 'Dia bloqueado')))}
                                        >
                                            {apps ? (
                                                apps.map(app => (
                                                    <div key={app.id} className="appointment-item-week" title={`${app.paciente?.nome} com ${getDoctorName(app.medico_id)}`}>
                                                        <Badge bg="primary" pill className="me-1">{app.paciente?.nome.split(' ')[0]}</Badge>
                                                        {(currentUserRole === 'Representante Legal' || currentUserRole === 'Recepção') && (
                                                            <small className="text-muted d-block">{getDoctorName(app.medico_id)}</small>
                                                        )}
                                                    </div>
                                                ))
                                            ) : (
                                                isPastDate ? '-' : <span className="text-success small">Vago</span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <Card className="shadow-sm h-100">
            <Card.Header>
                <Row className="align-items-center">
                    <Col>
                        <Card.Title as="h5" className="mb-0">
                            <span role="img" aria-label="calendário" className="me-2">🗓️</span>
                            Resumo da Agenda
                        </Card.Title>
                    </Col>
                    <Col xs="auto">
                        <ButtonGroup size="sm">
                            <Button variant={viewMode === 'day' ? 'primary' : 'outline-primary'} onClick={() => handleViewModeChange('day')}>Dia</Button>
                            <Button variant={viewMode === 'week' ? 'primary' : 'outline-primary'} onClick={() => handleViewModeChange('week')}>Semana</Button>
                        </ButtonGroup>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Button variant="outline-secondary" size="sm" onClick={handlePrev}>&lt; Anterior</Button>
                    <h6 className="mb-0">
                        {viewMode === 'day'
                            ? format(currentDate, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                            : `Semana de ${format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'dd/MM')} a ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'dd/MM/yyyy')}`
                        }
                    </h6>
                    <Button variant="outline-secondary" size="sm" onClick={handleNext}>Próximo &gt;</Button>
                </div>
                <div className="text-center mb-3">
                    <Button variant="info" size="sm" onClick={handleToday}>Ir para Hoje</Button>
                </div>

                {isLoading ? (
                    <div className="text-center"><Spinner animation="border" /></div>
                ) : error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : !hasBeenInitiated ? (
                    <div className="text-center text-muted mt-4">
                        <p>Clique em "Dia", "Semana" ou "Ir para Hoje" para carregar a agenda.</p>
                    </div>
                ) : (
                    viewMode === 'day' ? renderDayView() : renderWeekView()
                )}
            </Card.Body>
        </Card>
    );
};

export default ScheduleSummary;
